import React from 'react';
import Typography from '@mui/material/Typography';
import { Button, Modal } from '../../uikit';

interface SourceDiscoveryDialogProps {
  open: boolean;
  onDecline: () => void;
  onAccept: () => void;
  /** Set once the first click has been handled, so a lingering fade-out
   * frame can't be double-clicked/double-tapped. Purely visual - the actual
   * double-fire guard lives in the parent's ref-checked handlers, since a
   * prop only takes effect on the next render (COM-637 code review). */
  disabled?: boolean;
}

// COM-637: the opt-in gate in front of AI source discovery. Discovery is one
// OpenAI call per category and can take 5-10 minutes (see
// SourcesContext.handleStartSourceDiscovery / the design doc at
// docs/superpowers/specs/2026-08-15-com637-onboarding-wizard-design.md), so
// it must never run as a side effect of saving the profile - the user has
// to ask for it.
//
// Declining is the preferred path (most users add their own sources), so
// both buttons use the kit `Button` at equal size/weight - neither is a
// plain text link - and neither carries `autoFocus`. MUI's Dialog focus
// trap falls back to focusing its own container when no descendant
// requests focus, so leaving both buttons unfocused does not leave the
// dialog keyboard-inaccessible.
//
// Dismissing without choosing (backdrop click / Escape) routes to
// `onDecline`: declining has no side effect beyond closing (see
// ClientDataDialog's wiring), so this is the one dismiss behaviour that can
// never silently spend the user's money on an OpenAI call.
const SourceDiscoveryDialog: React.FC<SourceDiscoveryDialogProps> = ({
  open,
  onDecline,
  onAccept,
  disabled = false,
}) => {
  return (
    <Modal
      open={open}
      title="Find sources automatically?"
      onClose={onDecline}
      actions={
        <>
          <Button variant="primary" onClick={onAccept} disabled={disabled}>
            Yes, find sources for me
          </Button>
          <Button variant="secondary" onClick={onDecline} disabled={disabled}>
            No, I'll add sources myself
          </Button>
        </>
      }
    >
      <Typography fontSize={14} color="text.secondary">
        We can search for regulatory sources that match your profile. This can take 5–10 minutes.
      </Typography>
    </Modal>
  );
};

export default SourceDiscoveryDialog;
